import React, {
  createContext,
  Dispatch,
  PropsWithChildren,
  SetStateAction,
  useContext,
  useMemo,
  useState,
  useCallback,
  useEffect,
} from 'react';
import { useAppSelector } from '@common/hooks';
import * as loggedUserSelector from '@modules/core/selectors/logged-user';
import { selected } from '@modules/organisation/selectors/organisation';
import { useParams } from 'react-router';
import { fetchEvaluation } from '@modules/performance/action/fetch-evaluation';
import {
  EvaluationDetailResponse,
  EvaluationsResponse,
  PerformanceFormComponent,
  QuestionResponse,
} from '@modules/performance/types';
import {
  PerformanceQuestionType,
  EPerformanceActivityFilterTypes,
} from '@modules/performance/definitions';
import { updateEvaluation } from '@modules/performance/action/update-evaluation';
import { completeEvaluation as completeEvaluationApi } from '@modules/performance/action/complete-evaluation';
import { AlertService } from '@common/services/alert';
import { useTranslation } from 'react-i18next';

interface EvaluationStore {
  evaluation: EvaluationDetailResponse;
  setEvaluation: Dispatch<SetStateAction<any>>;
  handleFetchEvaluation: (evaluationId: string) => Promise<any>;
  loading: boolean;
  isDirty: boolean;
  isSavingDraft: boolean;
  isSubmittingEvaluation: boolean;
  isCompletingEvaluation: boolean;
  // competencies: any;
  componentsById: Record<string, any>;
  componentsByType: Record<string, any[]>;
  responsesByComponentId: Record<
    string,
    { answersByUserId: Record<string, any> }
  >;
  setComponentAnswer: (
    componentId: string,
    userId: string,
    answer: any
  ) => void;
  validationErrorsByComponentId: Record<string, string>;
  selectedCategoryIds: string[];
  filteredCompetencyGroups: any[];
  selectedRevieweeScaleByCompetency: Record<string, string>;
  selectedReviewerScaleByCompetency: Record<string, string>;
  setSelectedScale: (
    role: 'reviewee' | 'reviewer',
    competencyId: string,
    scaleId: string
  ) => void;
  getSelectedScaleId: (
    role: 'reviewee' | 'reviewer',
    competencyId: string,
    defaultScaleId?: string
  ) => string | undefined;
  submitEvaluation: (
    status: 'draft' | 'submitted',
    onSuccess?: () => void
  ) => Promise<void>;
  completeEvaluation: () => Promise<void>;
  handleCreateEvaluation: (
    formId: string,
    reviewerIds: string[],
    userId: string,
    organisationId: string
  ) => void;
  openSideBar: boolean;
  setOpenEvaluationSideBar: (toggle: boolean) => void;
  selectedFilter: string;
  handleFilterChange: (filterId: string) => void;
  evaluationReports: EvaluationsResponse[];
}

const EvaluationContext = createContext<EvaluationStore | null>(null);

export const EvaluationStoreProvider = ({
  children,
}: PropsWithChildren<{ basePath: string }>) => {
  const { t } = useTranslation();
  const organisation = useAppSelector(selected);
  const { id } = useParams<{ id: string }>();
  const [evaluation, setEvaluation] =
    useState<EvaluationDetailResponse | null>(null);
  const [loading, setLoading] = useState(false);
  const [isSavingDraft, setIsSavingDraft] = useState(false);
  const [isSubmittingEvaluation, setIsSubmittingEvaluation] = useState(false);
  const [isCompletingEvaluation, setIsCompletingEvaluation] = useState(false);
  const [responsesByComponentId, setResponsesByComponentId] = useState<
    Record<string, { answersByUserId: Record<string, any> }>
  >({});
  const [
    selectedRevieweeScaleByCompetency,
    setSelectedRevieweeScaleByCompetency,
  ] = useState<Record<string, string>>({});
  const [
    selectedReviewerScaleByCompetency,
    setSelectedReviewerScaleByCompetency,
  ] = useState<Record<string, string>>({});
  const [dirtyComponentIds, setDirtyComponentIds] = useState<
    Record<string, true>
  >({});
  const [validationErrorsByComponentId, setValidationErrorsByComponentId] =
    useState<Record<string, string>>({});
  const [openSideBar, setOpenSideBar] = useState(false);
  const [selectedFilter, setSelectedFilter] = useState<string>(
    EPerformanceActivityFilterTypes.NOTES
  );
  const loggedUser = useAppSelector(loggedUserSelector.selected);

  useEffect(() => {
    if (id) {
      handleFetchEvaluation(id);
    }
  }, [id]);

  const handleFetchEvaluation = useCallback(
    async (evaluationId: string) => {
      setLoading(true);
      const response = await fetchEvaluation(organisation.id, evaluationId);
      setEvaluation(response as any);
      setLoading(false);
      return response;
    },
    [organisation.id]
  );

  useEffect(() => {
    const answers: Record<string, { answersByUserId: Record<string, any> }> =
      {};
    const responseList: QuestionResponse[] =
      (evaluation as any)?.data?.responses || [];
    responseList.forEach((userResponse: QuestionResponse) => {
      const userId = String(userResponse?.user_id ?? '');
      const items: QuestionResponse['response'] = userResponse?.response || [];
      items.forEach((entry: QuestionResponse['response'][number]) => {
        const componentId = String(entry?.component_id ?? '');
        if (!componentId) return;

        if (
          entry?.type === 'competencies' &&
          typeof entry?.answer === 'object' &&
          'competency_id' in entry.answer
        ) {
          const competencyId = String(
            (entry.answer as { competency_id: string | number }).competency_id
          );
          if (!answers[competencyId]) {
            answers[competencyId] = { answersByUserId: {} };
          }
          answers[competencyId].answersByUserId[userId] = entry?.answer;
        }

        if (!answers[componentId]) {
          answers[componentId] = { answersByUserId: {} };
        }
        answers[componentId].answersByUserId[userId] = entry?.answer;
      });
    });
    setResponsesByComponentId(answers);
  }, [(evaluation as any)?.data?.responses]);

  const componentsById = useMemo(() => {
    const map: Record<string, PerformanceFormComponent> = {};
    const comps: PerformanceFormComponent[] =
      (evaluation as any)?.data?.form?.components || [];
    comps.forEach((c) => {
      if (c?.id != null) map[String(c.id)] = c;
    });
    return map;
  }, [evaluation]);

  const componentsByType = useMemo(() => {
    const groups: Record<string, PerformanceFormComponent[]> = {};
    const comps: PerformanceFormComponent[] =
      (evaluation as any)?.data?.form?.components || [];
    comps.forEach((c) => {
      const type = String(c?.type || 'unknown');
      if (!groups[type]) groups[type] = [];
      groups[type].push(c);
    });
    return groups;
  }, [evaluation]);

  const selectedCategoryIds = useMemo(() => {
    const comps: PerformanceFormComponent[] =
      (evaluation as any)?.data?.form?.components || [];
    const comp = comps.find(
      (c) => c?.type === PerformanceQuestionType.COMPETENCIES
    );
    const ids = comp?.settings?.category_ids || [];
    return Array.isArray(ids) ? ids.map((v: string) => String(v)) : [];
  }, [evaluation]);

  const filteredCompetencyGroups = useMemo(() => {
    // Get reviewee competencies directly from evaluation data
    const revieweeCompetencies =
      (evaluation as any)?.data?.reviewee_competencies || [];
    if (!revieweeCompetencies.length) return [] as any[];

    // Build category groups while preserving encounter order for categories and competencies
    const categoryMap: Record<
      string,
      { id: string; name: string; competencies: any[]; _idx: number }
    > = {};
    const encounteredCategoryIds: string[] = [];

    revieweeCompetencies.forEach((comp: any) => {
      const categoryId = String(comp?.category_id || comp?.category?.id || '');
      const categoryName = comp?.category?.name || 'Uncategorized';

      if (!categoryMap[categoryId]) {
        categoryMap[categoryId] = {
          id: categoryId,
          name: categoryName,
          competencies: [],
          _idx: encounteredCategoryIds.length,
        };
        encounteredCategoryIds.push(categoryId);
      }
      categoryMap[categoryId].competencies.push(comp);
    });

    const explicitOrder =
      Array.isArray(selectedCategoryIds) && selectedCategoryIds.length
        ? selectedCategoryIds.map((v: string) => String(v))
        : [];

    const orderedCategoryIds: string[] = [];
    explicitOrder.forEach((cid) => {
      if (categoryMap[cid]) orderedCategoryIds.push(cid);
    });
    encounteredCategoryIds.forEach((cid) => {
      if (!orderedCategoryIds.includes(cid)) orderedCategoryIds.push(cid);
    });

    return orderedCategoryIds.map((cid) => {
      const { _idx, ...group } = categoryMap[cid] as any;
      return group;
    });
  }, [evaluation, selectedCategoryIds]);

  const setComponentAnswer = useCallback(
    (componentId: string, userId: string, answer: any) => {
      setResponsesByComponentId((prev) => {
        const next = { ...prev };
        const compId = String(componentId);
        const uid = String(userId);
        const entry = next[compId] ?? { answersByUserId: {} };
        next[compId] = {
          answersByUserId: {
            ...entry.answersByUserId,
            [uid]: answer,
          },
        };
        return next;
      });
      setDirtyComponentIds((prev) => ({
        ...prev,
        [String(componentId)]: true,
      }));
      setValidationErrorsByComponentId((prev) => {
        const next = { ...prev };
        delete next[String(componentId)];
        return next;
      });
    },
    []
  );

  const setSelectedScale = useCallback(
    (role: 'reviewee' | 'reviewer', competencyId: string, scaleId: string) => {
      if (role === 'reviewee') {
        setSelectedRevieweeScaleByCompetency((prev) => ({
          ...prev,
          [String(competencyId)]: String(scaleId),
        }));
      } else {
        setSelectedReviewerScaleByCompetency((prev) => ({
          ...prev,
          [String(competencyId)]: String(scaleId),
        }));
      }
      // Mark as dirty for both reviewee and reviewer
      setDirtyComponentIds((prev) => ({
        ...prev,
        [String(competencyId)]: true,
      }));
      // Clear validation error when a competency rating is selected
      setValidationErrorsByComponentId((prev) => {
        const next = { ...prev };
        delete next[String(competencyId)];
        return next;
      });
    },
    []
  );

  const getSelectedScaleId = useCallback(
    (
      role: 'reviewee' | 'reviewer',
      competencyId: string,
      defaultScaleId?: string
    ) => {
      const compId = String(competencyId);
      const map =
        role === 'reviewee'
          ? selectedRevieweeScaleByCompetency
          : selectedReviewerScaleByCompetency;

      const explicit = map[compId];
      if (explicit) return explicit;

      try {
        const answersByUserId =
          responsesByComponentId?.[compId]?.answersByUserId;
        if (answersByUserId) {
          const userId =
            role === 'reviewee'
              ? String((evaluation as any)?.data?.reviewee?.id ?? '')
              : String((evaluation as any)?.data?.reviewers?.[0]?.id ?? '');
          const rawAnswer = userId ? answersByUserId[userId] : undefined;
          if (rawAnswer && typeof rawAnswer === 'object') {
            const nested: any = (rawAnswer as any).answer || rawAnswer;
            const scaleId = nested?.scale_id || nested?.scaleId;
            if (scaleId) return String(scaleId);
          }
        }
      } catch (_) {
        // no-op fallback to default below
      }

      return defaultScaleId;
    },
    [
      selectedRevieweeScaleByCompetency,
      selectedReviewerScaleByCompetency,
      responsesByComponentId,
      evaluation,
    ]
  );

  const submitEvaluation = useCallback(
    async (status: 'draft' | 'submitted', onSuccess?: () => void) => {
      const orgId = organisation.id;
      const evaluationId = String(id);
      if (!evaluationId || !evaluation) return;

      const reviewerId = String(
        (evaluation as any)?.data?.reviewers?.[0]?.id || ''
      );
      const revieweeId = String((evaluation as any)?.data?.reviewee?.id || '');
      const loggedUserId = String(loggedUser?.id || '');
      const isReviewerSubmitting = loggedUserId === reviewerId;
      const currentUserId = isReviewerSubmitting ? reviewerId : revieweeId;

      if (status === 'submitted') {
        try {
          const requiredErrors: Record<string, string> = {};
          const openQuestions: any[] =
            (componentsByType as any)?.open_question || [];
          openQuestions.forEach((oq: any) => {
            const cid = String(oq?.id || '');
            const isRequired = Boolean(oq?.settings?.answer_required);
            const visibility = oq?.settings?.visibility || {};
            const employeeOnly =
              Boolean(visibility?.employee) && !visibility?.manager;
            const managerOnly =
              Boolean(visibility?.manager) && !visibility?.employee;

            // Check visibility based on who is submitting
            if (isReviewerSubmitting && employeeOnly) return; // Reviewer can't answer employee-only questions (validation skip)
            if (!isReviewerSubmitting && managerOnly) return; // Reviewee can't see manager-only questions

            if (!cid || !isRequired || !currentUserId) return;
            const entry = responsesByComponentId?.[cid];
            const answer = entry?.answersByUserId?.[currentUserId];
            const val = typeof answer === 'string' ? answer.trim() : '';
            if (!val) {
              requiredErrors[cid] = t('performance:this_question_is_required');
            }
          });
          // Validate competencies - only validate if there are competencies components in the form
          const competenciesComponents = componentsByType?.competencies || [];
          if (
            competenciesComponents.length > 0 &&
            filteredCompetencyGroups.length > 0
          ) {
            const allCompetencies: any[] = filteredCompetencyGroups.flatMap(
              (g: any) => g?.competencies || []
            );
            allCompetencies.forEach((competency: any) => {
              const compId = String(competency?.id || '');
              if (!compId || !currentUserId) return;

              const role = isReviewerSubmitting ? 'reviewer' : 'reviewee';
              const selectedScaleId = getSelectedScaleId(role, compId);
              const raw =
                responsesByComponentId?.[compId]?.answersByUserId?.[
                  currentUserId
                ];
              const answerObj: any =
                raw && typeof raw === 'object'
                  ? (raw as any).answer || raw
                  : undefined;
              const existingScaleId: string | undefined =
                answerObj?.scale_id || answerObj?.scaleId;
              const effectiveScaleId = selectedScaleId || existingScaleId;

              if (!effectiveScaleId) {
                requiredErrors[compId] = t(
                  'performance:competency_rating_required'
                );
              }
            });
          }

          if (Object.keys(requiredErrors).length > 0) {
            setValidationErrorsByComponentId(requiredErrors);
            AlertService.warning(
              t('performance:please_complete_all_required_fields')
            );
            return;
          }
        } catch (_) {
          // noop
        }
      }

      const responses: Array<{ component_id: string; answer: any }> = [];
      const dirtyIds = Object.keys(dirtyComponentIds || {});

      try {
        const openQuestions: any[] =
          (componentsByType as any)?.open_question || [];
        const openSet = new Set<string>(
          openQuestions.map((oq: any) => String(oq?.id || ''))
        );
        dirtyIds.forEach((cid) => {
          if (!openSet.has(cid) || !currentUserId) return;
          const entry = responsesByComponentId?.[cid];
          const answer = entry?.answersByUserId?.[currentUserId];
          if (typeof answer === 'string') {
            responses.push({ component_id: cid, answer });
          }
        });
      } catch (_) {
        // noop
      }

      try {
        const allCompetencies: any[] = filteredCompetencyGroups.flatMap(
          (g: any) => g?.competencies || []
        );
        const compSet = new Set<string>(
          allCompetencies.map((c: any) => String(c?.id || ''))
        );
        const competenciesComponentId = String(
          (componentsByType as any)?.competencies?.[0]?.id || ''
        );
        dirtyIds.forEach((compId) => {
          if (!compSet.has(compId) || !currentUserId) return;
          const role = isReviewerSubmitting ? 'reviewer' : 'reviewee';
          const selectedScaleId = getSelectedScaleId(role, compId);
          const raw =
            responsesByComponentId?.[compId]?.answersByUserId?.[currentUserId];
          const answerObj: any =
            raw && typeof raw === 'object'
              ? (raw as any).answer || raw
              : undefined;
          const existingScaleId: string | undefined =
            answerObj?.scale_id || answerObj?.scaleId;
          const comment: string | undefined = answerObj?.comment;
          const effectiveScaleId = selectedScaleId || existingScaleId;
          if (effectiveScaleId || (comment && comment.length > 0)) {
            responses.push({
              component_id: competenciesComponentId,
              answer: {
                competency_id: compId,
                scale_id: effectiveScaleId
                  ? String(effectiveScaleId)
                  : undefined,
                comment: comment || undefined,
              },
            });
          }
        });
      } catch (_) {
        // noop
      }

      // Handle talent review submissions
      try {
        const talentReviews: any[] =
          (componentsByType as any)?.talent_review || [];
        const talentReviewSet = new Set<string>(
          talentReviews.map((tr: any) => String(tr?.id || ''))
        );
        dirtyIds.forEach((cid) => {
          if (!talentReviewSet.has(cid) || !currentUserId) return;
          const entry = responsesByComponentId?.[cid];
          const raw = entry?.answersByUserId?.[currentUserId];
          // Handle both direct answer and nested answer format
          let answerData = raw;
          if (raw && typeof raw === 'object' && raw.answer) {
            answerData = raw.answer;
          }
          const answerObj = answerData && typeof answerData === 'object' ? answerData : {};
          responses.push({
            component_id: cid,
            answer: {
              potential: answerObj.potential ?? 2,
              performance: answerObj.performance ?? 2,
              retention_risk: answerObj.retention_risk ?? 2,
              impact_of_loss: answerObj.impact_of_loss || null,
            },
          });
        });
      } catch (_) {
        // noop
      }

      const payload = {
        submission_status: status,
        response: responses,
      } as any;

      const setSubmitting = (flag: boolean) => {
        if (status === 'submitted') {
          setIsSubmittingEvaluation(flag);
        } else {
          setIsSavingDraft(flag);
        }
      };

      setSubmitting(true);
      try {
        await updateEvaluation(orgId, evaluationId, payload);
        if (status === 'submitted') {
          setEvaluation((prev) => {
            if (!prev) return prev as any;
            const userId = String(loggedUser?.id || '');
            const next: any = {
              ...(prev as any),
              data: { ...(prev as any).data },
            };
            // Update reviewer status if the logged user is among reviewers
            if (Array.isArray(next.data?.reviewers)) {
              next.data.reviewers = next.data.reviewers.map((r: any) => (String(r?.id || '') === userId
                  ? {
                      ...r,
                      status: 'submitted',
                      submitted_at: r?.submitted_at || new Date().toISOString(),
                    }
                  : r)
              );
            }
            // Update reviewee status if the logged user is the reviewee
            if (String(next.data?.reviewee?.id || '') === userId) {
              next.data.reviewee = {
                ...next.data.reviewee,
                status: 'submitted',
                submitted_at:
                  next.data.reviewee?.submitted_at || new Date().toISOString(),
              };
            }
            return next;
          });
        }
        AlertService.success(
          status === 'submitted'
            ? t('performance:review_submitted')
            : t('performance:draft_saved')
        );
        // Clear dirty state after successful save
        setDirtyComponentIds({});
        // Call success callback if provided
        if (onSuccess && status === 'submitted') {
          onSuccess();
        }
      } catch (error: any) {
        AlertService.forStatus(error?.status_code, {
          warning: t('performance:could_not_save_review'),
          error: t('performance:submission_failed'),
        });
        throw error;
      } finally {
        setSubmitting(false);
      }
    },
    [
      organisation.id,
      id,
      evaluation,
      componentsByType,
      responsesByComponentId,
      filteredCompetencyGroups,
      getSelectedScaleId,
      dirtyComponentIds,
    ]
  );

  const completeEvaluation = useCallback(async () => {
    const orgId = organisation.id;
    const evaluationId = String(id);
    if (!evaluationId || !evaluation) return;

    setIsCompletingEvaluation(true);
    try {
      await completeEvaluationApi(orgId, evaluationId, {
        submissionStatus: 'completed',
      });
      setEvaluation((prev) => {
        if (!prev) return prev as any;
        const next: any = { ...(prev as any), data: { ...(prev as any).data } };
        next.data.completed_at = new Date().toISOString();
        return next;
      });
      AlertService.success(t('performance:review_completed'));
    } catch (error: any) {
      AlertService.forStatus(error?.status_code, {
        warning: t('performance:could_not_complete_review'),
        error: t('performance:submission_failed'),
      });
      throw error;
    } finally {
      setIsCompletingEvaluation(false);
    }
  }, [organisation.id, id, evaluation, t]);

  const setOpenEvaluationSideBar = useCallback((value: boolean) => {
    setOpenSideBar(value);
  }, []);

  const handleFilterChange = useCallback((filterId: string) => {
    setSelectedFilter(filterId);
  }, []);

  const contextValue = useMemo(
    () => ({
      evaluation,
      setEvaluation,
      handleFetchEvaluation,
      loading,
      isDirty: Object.keys(dirtyComponentIds || {}).length > 0,
      isSavingDraft,
      isSubmittingEvaluation,
      isCompletingEvaluation,
      // competencies,
      componentsById,
      componentsByType,
      responsesByComponentId,
      setComponentAnswer,
      validationErrorsByComponentId,
      selectedCategoryIds,
      filteredCompetencyGroups,
      selectedRevieweeScaleByCompetency,
      selectedReviewerScaleByCompetency,
      setSelectedScale,
      getSelectedScaleId,
      submitEvaluation,
      completeEvaluation,
      setOpenEvaluationSideBar,
      openSideBar,
      selectedFilter,
      handleFilterChange,
      // evaluationReports
    }),
    [
      evaluation,
      setEvaluation,
      handleFetchEvaluation,
      loading,
      dirtyComponentIds,
      isSavingDraft,
      isSubmittingEvaluation,
      isCompletingEvaluation,
      // competencies,
      componentsById,
      componentsByType,
      responsesByComponentId,
      setComponentAnswer,
      validationErrorsByComponentId,
      selectedCategoryIds,
      filteredCompetencyGroups,
      selectedRevieweeScaleByCompetency,
      selectedReviewerScaleByCompetency,
      setSelectedScale,
      getSelectedScaleId,
      submitEvaluation,
      completeEvaluation,
      setOpenEvaluationSideBar,
      openSideBar,
      selectedFilter,
      handleFilterChange,
      // evaluationReports
    ]
  );

  return (
    <EvaluationContext.Provider
      value={contextValue as unknown as EvaluationStore}
    >
      {children}
    </EvaluationContext.Provider>
  );
};

export const useEvaluationStore = (): EvaluationStore => {
  const context = useContext(EvaluationContext);
  if (!context) {
    throw new Error(
      'useEvaluationStore must be used within a EvaluationStoreProvider'
    );
  }
  return context;
};

export const withEvaluationStore =
  (Component: React.ComponentType<any>) => (props: any) => (
    <EvaluationStoreProvider basePath={props.basePath}>
      <Component {...props} />
    </EvaluationStoreProvider>
    );
